import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiArrowRight,
  FiCheck,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import styles from "./SMSCode.module.css";
import api from "../../services/axiosConfig";
import { OTP_SEND, OTP_VERIFIED } from "../../constant-value";



const SMSCode = ({ nextStep, prevStep, values }) => {
  const navigate = useNavigate();
  const inputsRef = useRef([]);
  const timerRef = useRef(null);

  const [cooldown, setCooldown] = useState(180);
  const [errorShake, setErrorShake] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [otpState, setOtpState] = useState({
    fullName: values.name,
    phoneNumber: values.mobileNumber,
    password: values.password,
    dateOfBirth: "2025-05-01T12:44:53.597Z",
    otpCode: "",
  });

  // -----------------------------
  // Timer
  // -----------------------------

  const startTimer = () => {
    clearInterval(timerRef.current);

    setCooldown(180);

    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startTimer();

    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  // -----------------------------
  // Web OTP - Android Auto Fill
  // -----------------------------

  useEffect(() => {
    if (!("OTPCredential" in window)) return;

    const controller = new AbortController();

    navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: controller.signal,
      })
      .then((credential) => {
        if (!credential?.code) return;

        const code = credential.code
          .replace(/\D/g, "")
          .slice(0, 4);

        code.split("").forEach((digit, index) => {
          if (inputsRef.current[index]) {
            inputsRef.current[index].value = digit;
          }
        });

        setOtpState((prev) => ({
          ...prev,
          otpCode: code,
        }));

        inputsRef.current[3]?.focus();
      })
      .catch(() => {});

    return () => controller.abort();
  }, []);

  // -----------------------------
  // OTP Input
  // -----------------------------

  const handleInput = (event, index) => {
    const value = event.target.value
      .replace(/\D/g, "")
      .slice(0, 1);

    event.target.value = value;

    if (navigator.vibrate) {
      navigator.vibrate(20);
    }

    const code = inputsRef.current
      .map((input) => input?.value || "")
      .join("");

    setOtpState((prev) => ({
      ...prev,
      otpCode: code,
    }));

    if (value && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (
      event.key === "Backspace" &&
      !event.target.value &&
      index > 0
    ) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  // -----------------------------
  // Clear OTP
  // -----------------------------

  const clearOtp = () => {
    inputsRef.current.forEach((input) => {
      if (input) input.value = "";
    });

    setOtpState((prev) => ({
      ...prev,
      otpCode: "",
    }));

    inputsRef.current[0]?.focus();
  };

  // -----------------------------
  // OTP Error
  // -----------------------------

  const showOtpError = (message) => {
    toast.warning(message);

    setErrorShake(true);

    setTimeout(() => {
      setErrorShake(false);
    }, 500);

    clearOtp();
  };

  // -----------------------------
  // Verify OTP
  // -----------------------------

  const verifyOTP = () => {
    if (otpState.otpCode.length !== 4) {
      toast.warning("لطفاً کد ۴ رقمی را کامل وارد کنید.");
      return;
    }

    if (isVerifying) return;

    setIsVerifying(true);

    api
      .post(`/${OTP_VERIFIED}`, otpState)
      .then((res) => {
        const data = res.data;

        if (data.isSuccess) {
          toast.success(data.message);
          navigate("/login");
          return;
        }

        showOtpError(data.message);
      })
      .catch((error) => {
        console.log("OTP VERIFY ERROR:", error);

        showOtpError(
          error.response?.data?.message ||
            "خطایی در تأیید کد رخ داده است."
        );
      })
      .finally(() => {
        setIsVerifying(false);
      });
  };

  const continueHandler = (event) => {
    event.preventDefault();
    verifyOTP();
  };

  // -----------------------------
  // Re-send OTP
  // -----------------------------

  const reSendOtp = () => {
    if (cooldown > 0 || isResending) return;

    setIsResending(true);

    api
      .post(`/${OTP_SEND}`, {
        phoneNumber: values.mobileNumber,
      })
      .then((res) => {
        const data = res.data;

        if (data.isSuccess) {
          toast.success(data.message);
          startTimer();
          clearOtp();
          return;
        }

        toast.warning(data.message);
      })
      .catch((error) => {
        console.log("OTP RESEND ERROR:", error);

        toast.warning(
          error.response?.data?.message ||
            "امکان ارسال مجدد کد وجود ندارد."
        );
      })
      .finally(() => {
        setIsResending(false);
      });
  };

  const reSendHandler = (event) => {
    event.preventDefault();
    reSendOtp();
  };

  // -----------------------------
  // UI
  // -----------------------------

  return (
    <div className={styles.page} dir="rtl">
      <div className={styles.container}>

        {/* Header */}

        <div className={styles.header}>
          <div className={styles.iconBox}>
            <FiShield />
          </div>

          <h1>تأیید شماره همراه</h1>

          <p>
            کد ۴ رقمی به شماره
            <strong>{values.mobileNumber}</strong>
            ارسال شد
          </p>
        </div>

        {/* OTP */}

        <div
          className={`${styles.otpWrapper} ${
            errorShake ? styles.shake : ""
          }`}
          dir="ltr"
        >
          {[0, 1, 2, 3].map((index) => (
            <input
              key={index}
              ref={(element) => {
                inputsRef.current[index] = element;
              }}
              type="text"
              maxLength={1}
              inputMode="numeric"
              autoComplete={
                index === 0 ? "one-time-code" : "off"
              }
              className={styles.otpInput}
              onChange={(event) =>
                handleInput(event, index)
              }
              onKeyDown={(event) =>
                handleKeyDown(event, index)
              }
            />
          ))}
        </div>

        {/* Verify */}

        <button
          type="button"
          className={styles.verifyButton}
          onClick={continueHandler}
          disabled={isVerifying}
        >
          {isVerifying ? (
            <>
              <span className={styles.spinner} />
              در حال بررسی...
            </>
          ) : (
            <>
              <FiCheck />
              تأیید و ادامه
            </>
          )}
        </button>

        {/* Actions */}

        <div className={styles.actions}>

          <button
            type="button"
            className={styles.editButton}
            onClick={prevStep}
          >
            <FiArrowRight />
            اصلاح شماره
          </button>

          <button
            type="button"
            onClick={reSendHandler}
            disabled={cooldown > 0 || isResending}
            className={styles.resendButton}
          >
            <FiRefreshCw
              className={
                isResending ? styles.rotating : ""
              }
            />

            {cooldown > 0
              ? `ارسال مجدد ${cooldown} ثانیه`
              : isResending
              ? "در حال ارسال..."
              : "ارسال مجدد کد"}
          </button>

        </div>

        {/* Hint */}

        <div className={styles.hint}>
          <span>کد را دریافت نکردید؟</span>
          <span>
            پوشه پیامک‌های مسدود یا شماره واردشده را بررسی کنید.
          </span>
        </div>

      </div>
    </div>
  );
};

export default SMSCode;